import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Box, Zap, ShieldCheck, Home as HomeIcon, Check } from 'lucide-react';
import AnimatedSection from '../components/AnimatedSection';
import './HowItWorks.css';

const steps = [
  {
    num: '01',
    icon: <Box />,
    title: 'Design Your Home',
    subtitle: '15 minutes to your dream home',
    desc: 'Enter your plot dimensions and start designing with your personal AI architect. Just describe what you want via text or voice, it is as easy as chatting on WhatsApp and watch your home come alive in real-time 3D.',
    features: [
      'Enter plot size and orientation',
      'Choose from 100+ ready templates or start from scratch',
      'AI architect builds your design from text or voice input',
      'Real-time 3D visualization as you design',
      'AI suggestions for optimal room layouts',
      'Upload existing blueprints - AI converts them to 3D',
      'Instant cost estimate updates as you add rooms',
    ],
    img: '/screenshots/design-studio.png',
    duration: '15-30 minutes',
  },
  {
    num: '02',
    icon: <Zap />,
    title: 'Finalize Your Plan',
    subtitle: 'Every detail, every rupee - planned',
    desc: 'Compare multiple designs, get detailed bill of quantities, MEP planning with AI clash detection, Vastu compliance check, and downloadable construction-ready blueprints.',
    features: [
      'Compare up to 5 designs side by side',
      'Detailed bill of quantities - every material, every cost',
      'MEP planning: electrical, plumbing, HVAC layouts',
      'AI clash detection - catches conflicts before construction',
      'Vastu compliance checker with suggestions',
      'Download construction-ready blueprints (PDF & DWG)',
      'Consult with a Sculpit architect via video call',
    ],
    img: '/screenshots/Realtime-cost-estimator.PNG',
    duration: '1-2 weeks',
  },
  {
    num: '03',
    icon: <ShieldCheck />,
    title: 'Build with Confidence',
    subtitle: 'Verified builders, protected payments',
    desc: 'Sculpit assigns a verified construction partner. Track everything in real-time with milestone-based payments, quality inspections, and predictive delay alerts.',
    features: [
      'Sculpit auto-assigns the best verified partner',
      'RERA registered, ISO quality certified builders',
      'Milestone-based escrow payments - safe & transparent',
      'Real-time project tracking dashboard',
      'Predictive delay alerts 3-5 days in advance',
      '15+ mandatory quality inspections at every stage',
      'All government approvals and compliance handled',
    ],
    img: '/screenshots/verified-construction-partners.png',
    duration: '6-12 months',
  },
  {
    num: '04',
    icon: <HomeIcon />,
    title: 'Move In',
    subtitle: 'Welcome to your dream home',
    desc: 'Utility connections, final inspections, occupancy certificate - all handled. Your interiors are ready, keys are in your hand. Welcome home.',
    features: [
      'Utility connections: water, electricity, gas',
      'Final quality inspection and snag list resolution',
      'Occupancy certificate obtained',
      'Interior setup coordinated',
      'Home warranty documentation',
      'Maintenance guide for your new home',
      'Welcome home celebration!',
    ],
    img: '/screenshots/landing.png',
    duration: '2-4 weeks',
  },
];

const timeline = [
  { phase: 'Design & Planning', duration: '2-4 weeks', percent: 10 },
  { phase: 'Foundation', duration: '3-4 weeks', percent: 15 },
  { phase: 'Plinth & Structure', duration: '6-8 weeks', percent: 25 },
  { phase: 'Framing & Walls', duration: '4-6 weeks', percent: 20 },
  { phase: 'MEP (Electrical, Plumbing)', duration: '3-4 weeks', percent: 10 },
  { phase: 'Finishing & Interiors', duration: '4-6 weeks', percent: 15 },
  { phase: 'Final Inspection & Handover', duration: '1-2 weeks', percent: 5 },
];

export default function HowItWorks() {
  return (
    <div className="hiw-page">
      {/* Hero */}
      <section className="hiw-hero section-dark">
        <div className="hiw-hero__bg">
          <div className="hiw-hero__orb hiw-hero__orb--1" />
          <div className="hiw-hero__orb hiw-hero__orb--2" />
        </div>
        <div className="container hiw-hero__content">
          <AnimatedSection>
            <span className="label label-teal">Step by Step</span>
            <h1>From Dream to <span className="text-gradient">Dream Home</span></h1>
            <p className="hiw-hero__subtitle">
              Four simple steps. One trusted platform. Zero stress. Here's exactly how Sculpit takes you from first sketch to front door.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Steps */}
      {steps.map((step, i) => (
        <section key={i} className={`section ${i % 2 !== 0 ? 'section-gray' : ''}`}>
          <div className="container">
            <div className={`hiw-detail-row ${i % 2 !== 0 ? 'hiw-detail-row--reverse' : ''}`}>
              <AnimatedSection direction={i % 2 === 0 ? 'left' : 'right'}>
                <div className="hiw-detail__content">
                  <div className="hiw-detail__num">{step.num}</div>
                  <div className="icon-wrap icon-wrap--teal icon-wrap--lg">{step.icon}</div>
                  <h2>{step.title}</h2>
                  <p className="hiw-detail__subtitle">{step.subtitle}</p>
                  <p className="hiw-detail__desc">{step.desc}</p>
                  <ul className="hiw-detail__features">
                    {step.features.map((f, j) => (
                      <li key={j}><Check size={16} className="hiw-check" /> {f}</li>
                    ))}
                  </ul>
                  <div className="hiw-detail__duration">
                    <span>Typical duration:</span> <strong>{step.duration}</strong>
                  </div>
                </div>
              </AnimatedSection>
              <AnimatedSection direction={i % 2 === 0 ? 'right' : 'left'}>
                <div className="hiw-detail__visual">
                  <div className="hiw-detail__img-wrap card-premium">
                    <img src={step.img} alt={step.title} />
                  </div>
                </div>
              </AnimatedSection>
            </div>
          </div>
        </section>
      ))}

      {/* Timeline */}
      <section className="section section-dark">
        <div className="container">
          <AnimatedSection>
            <div className="section-header">
              <span className="label label-teal">Project Timeline</span>
              <h2>Typical Construction Timeline</h2>
              <p>
                For a standard 1,500-2,000 sq ft home. Actual timelines may vary based on design complexity and location.
              </p>
            </div>
          </AnimatedSection>

          <AnimatedSection>
            <div className="hiw-timeline-bars">
              {timeline.map((t, i) => (
                <div key={i} className="hiw-timeline-bar">
                  <div className="hiw-timeline-bar__info">
                    <span className="hiw-timeline-bar__phase">{t.phase}</span>
                    <span className="hiw-timeline-bar__dur">{t.duration}</span>
                  </div>
                  <div className="hiw-timeline-bar__track">
                    <div className="hiw-timeline-bar__fill" style={{ width: `${t.percent * 3}%` }} />
                  </div>
                </div>
              ))}
              <div className="hiw-timeline-total">
                <strong>Total: 6-9 months</strong> for a typical residential home
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* CTA */}
      <section className="section" style={{ textAlign: 'center' }}>
        <div className="container">
          <AnimatedSection>
            <h2>Ready to Start Your Journey?</h2>
            <p className="cta-subtitle">
              Design your dream home in 3D - for free. No credit card, no commitment.
            </p>
            <Link to="/pricing" className="btn btn-primary btn-lg">
              Start Designing Free <ArrowRight size={18} />
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
